import React, { useState, useEffect, useRef } from "react";
import styles from "./index.module.scss";
import SSOLogin from "./utils";
import { useMediaQuery } from "~hooks/useMediaQuery";
import cn from "classnames";
import { SVG_LIBRARY } from "~components/Svg";

const Header = (props) => {
  const [userInfo, setUserInfo] = useState(null);
  const ssoLogin = useRef(null);
  const desktopSDK = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const [showOverlay, setShowOverlay] = useState(false);
  const headerData = props.pageData?.header;
  const [transparent, setTransparent] = useState(false);
  const [domLoaded, setDomLoaded] = useState(false);
  let hostname, currPath, transparentPage;

  // turn this backon when the desktopSDK is ready
  const isDesktop = useMediaQuery();
  const isMobile = useMediaQuery("(max-width: 719px)");
  const isTablet = useMediaQuery("(min-width: 720px) and (max-width: 1023px)");

  const onLoginSuccess = (user) => {
    setUserInfo(user);
    props.onLogin && props.onLogin(user);
    props.onSDKLogin && props.onSDKLogin(user);
    // To handle the case when user visit force login page directly
    if (props.forceLogin) {
      window.location.reload();
    }
  };

  const onLogout = async () => {
    if (ssoLogin) {
      if (isDesktop && window.alpsLoginSDKInstance) {
        await new Promise((resolve) => {
          window.alpsLoginSDKInstance.logout(true).then((res) => {
            resolve(res.resultCode);
          });
        });
      }
      ssoLogin.current.handleLogout();
      setUserInfo(null);
      if (ssoLogin.current.props.forceLogin && isDesktop) {
        window.location.reload();
      }
    }
  };

  const onVisibilityChange = (visible) => {
    props.onSDKVisibilityChange && props.onSDKVisibilityChange(visible);
  };

  const onHamburgerButtonClick = (flag) => {
    document.documentElement.style.overflow = flag ? "hidden" : "auto";
    // Prevent background layer scrolling
    if (flag) {
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.position = "fixed";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
    setShowOverlay(flag);
  };

  useEffect(() => {
    if (!isDesktop || props.disableSSO) return;
    // Check if window.AlpsLoginSDK exists before loading
    let loginScript;
    if (!window.AlpsLoginSDK) {
      loginScript = document.createElement("script");
      loginScript.src = headerData.meta.ssoSDK.desktop;
      loginScript.async = true;
      loginScript.onload = () => {
        if (typeof AlpsLoginSDK !== "undefined") {
          desktopSDK.current = new AlpsLoginSDK();
          desktopSDK.current.init({
            onLoginSuccess: onLoginSuccess,
            onVisibilityChange: onVisibilityChange,
          });
          window.alpsLoginSDKInstance = desktopSDK.current;
          setScriptLoaded(true);
          props.onSDKInitialized && props.onSDKInitialized();
        } else {
          console.error("AlpsLoginSDK is not defined.");
        }
      };
      loginScript.onerror = () => {
        console.error("AlpsLoginSDK failed to load.");
      };
      if (loginScript && document?.head) {
        document.head.appendChild(loginScript);
      }
    }

    return () => {
      if (loginScript && document?.head) {
        document.head.removeChild(loginScript);
      }
    };
  }, [isDesktop]);

  useEffect(() => {
    ssoLogin.current = new SSOLogin(props);
    ssoLogin.current.checkUserInfo();
    setUserInfo(ssoLogin.current.getUserInfo());
  }, [scriptLoaded]);

  useEffect(() => {
    hostname = window.location.hostname;
    currPath = window.location.pathname;
    if (
      headerData?.meta?.transparent?.origins?.includes(hostname) &&
      headerData?.meta?.transparent?.paths?.includes(currPath)
    ) {
      transparentPage = true;
      setTransparent(true);
    }
    window.addEventListener("scroll", handleHeaderScroll);
    return () => {
      window.removeEventListener("scroll", handleHeaderScroll);
    };
  }, []);

  useEffect(() => {
    if (isMobile || isTablet) {
      setDomLoaded(true);
    } else if (isDesktop && scriptLoaded) {
      if (!domLoaded) {
        setDomLoaded(true);
      }
    }
  }, [scriptLoaded, isMobile, isTablet]);

  const handleHeaderScroll = () => {
    const fromTop = window.scrollY;
    if (transparentPage) {
      if (fromTop > 0) {
        setTransparent(false);
      } else {
        setTransparent(true);
      }
    }
  };

  return (
    <div
      className={cn(styles.alpsHeader, {
        [styles[`alpsHeaderTheme${headerData?.theme}`]]: headerData?.theme,
        [styles.alpsHeaderTransparent]: transparent,
        [styles.alpsHeaderOverlayActive]: showOverlay,
        [styles.hidden]: !domLoaded,
      })}
      id="alpsHeaderId"
    >
      <div className={cn(styles.alpsHeaderContent)}>
        <div
          className={cn(
            styles.alpsHeaderContentInner,
            "grid-container-grid grid-row"
          )}
        >
          <HeaderLogo
            link={headerData?.meta.link}
            title={headerData?.meta.title}
          />
          <HeaderMenus data={headerData?.menu} />
          <HeaderAccount
            desktopSDK={desktopSDK}
            userInfo={userInfo}
            account={headerData?.account}
            onLogout={onLogout}
          />
          <HeaderButtons data={headerData?.buttons} />
          <HamburgerMenu
            onClick={onHamburgerButtonClick}
            status={showOverlay}
          />
          <HeaderOverlay
            {...headerData}
            userInfo={userInfo}
            active={showOverlay}
            ssoLogin={ssoLogin}
            onLogout={onLogout}
          />
        </div>
      </div>
    </div>
  );
};

const HamburgerMenu = ({ onClick, status }) => {
  const onClickHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick(!status);
  };
  return (
    <div className={styles.alpsHeaderHamburger} onClick={onClickHandler}>
      {SVG_LIBRARY[status ? "iconClose" : "iconHamburger"]}
    </div>
  );
};

const HeaderLogo = ({ link, title }) => {
  return (
    <a className={styles.alpsHeaderLogo} href={link} title={title} alt={title}>
      {SVG_LIBRARY["logoWordmarkWhite"]}
    </a>
  );
};

const HeaderAccount = ({ userInfo, desktopSDK, account, onLogout }) => {
  const onClickHandler = () => {
    userInfo ? null : desktopSDK.current?.show();
  };

  return (
    <div className={styles.alpsHeaderAccountButton}>
      <div
        className={cn(styles.alpsHeaderAccountButtonAvatar, {
          [styles.alpsHeaderAccountButtonLogin]: userInfo,
        })}
        onClick={onClickHandler}
      >
        {userInfo ? <img src={userInfo.avatar} /> : SVG_LIBRARY["iconLogin"]}
      </div>
      {userInfo && (
        <div className={styles.alpsHeaderAccountLinksWrapper}>
          <HeaderAccountLinks data={account.links} onLogout={onLogout} />
        </div>
      )}
    </div>
  );
};

const HeaderConsultDialog = ({ data, onClose }) => {
  return (
    <div className={styles.alpsHeaderConsultContact}>
      <div className={styles.alpsHeaderConsultContactInner}>
        <i className={styles.alpsHeaderConsultClose} onClick={onClose}>
          {SVG_LIBRARY["iconClose"]}
        </i>
        <div className={styles.alpsHeaderConsultContactQRCode}>
          <h2 dangerouslySetInnerHTML={{ __html: data?.title }} />
          <img src={data?.qrcode} />
        </div>
        <div className={styles.alpsHeaderConsultContactFellow}>
          <img src={data?.fellow.avatar} />
          <div>
            <h3 dangerouslySetInnerHTML={{ __html: data?.fellow.title }} />
            <i>{SVG_LIBRARY["onvoPill"]}</i>
            <p
              dangerouslySetInnerHTML={{
                __html: data?.fellow.description,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const HeaderOverlay = ({
  menu,
  account,
  buttons,
  userInfo,
  active,
  ssoLogin,
  onLogout,
}) => {
  const [showAccountOverlay, setShowAccountOverlay] = useState(false);
  const [showConsultOverlay, setShowConsultOverlay] = useState(false);
  return (
    <div className={cn(styles.alpsHeaderOverlay, active ? styles.active : "")}>
      <HeaderMenus data={menu} />
      <div
        className={styles.alpsHeaderAccountMenu}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (userInfo) {
            setShowAccountOverlay(true);
          } else {
            ssoLogin.current.handleLogin();
          }
        }}
      >
        <i>{SVG_LIBRARY["iconLogin"]}</i>
        <span>{account?.status[userInfo ? "login" : "default"]}</span>
      </div>
      <HeaderButtons
        data={buttons}
        onConsultButtonClicked={() => setShowConsultOverlay(true)}
      />
      {showAccountOverlay && (
        <div className={styles.alpsHeaderAccountOverlay}>
          <div className={styles.alpsHeaderAccountOverlayInner}>
            <div className={styles.alpsHeaderAccountOverlayClose}>
              <i onClick={() => setShowAccountOverlay(false)}>
                {SVG_LIBRARY["iconClose"]}
              </i>
            </div>
            <HeaderAccountLinks data={account.links} onLogout={onLogout} />
          </div>
        </div>
      )}
      {showConsultOverlay && (
        <HeaderConsultDialog
          data={buttons?.consult?.dialog}
          onClose={() => setShowConsultOverlay(false)}
        />
      )}
    </div>
  );
};

const HeaderAccountLinks = ({ data, onLogout }) => {
  return (
    <ul className={styles.alpsHeaderAccountLinks}>
      {data?.map(({ icon, href, label, id }) => {
        return (
          <li key={id}>
            <a
              href={href}
              title={label}
              onClick={(e) => {
                if (id === "logout") {
                  e.preventDefault();
                  e.stopPropagation();
                  onLogout();
                }
              }}
            >
              <i>{SVG_LIBRARY[icon]}</i>
              <span>{label}</span>
            </a>
          </li>
        );
      })}
    </ul>
  );
};

const HeaderButton = ({ href, label }) => {
  return (
    <a className={cn(styles.alpsHeaderButton)} title={label} href={href}>
      {label}
    </a>
  );
};

const HeaderConsultButton = ({ label, dialog, onClick = () => {} }) => {
  const onClickHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };
  return (
    <div className={styles.alpsHeaderConsultButtonWrapper}>
      <a
        className={styles.alpsHeaderButton}
        title={label}
        onClick={onClickHandler}
      >
        {label}
      </a>
      <HeaderConsultDialog data={dialog} />
    </div>
  );
};

const HeaderButtons = ({ data, onConsultButtonClicked }) => {
  return (
    <div className={cn(styles.alpsHeaderButtons)}>
      <HeaderConsultButton
        {...data?.consult}
        onClick={onConsultButtonClicked}
      />
      <HeaderButton {...data?.testDrive} />
    </div>
  );
};

const HeaderMenus = ({ data }) => {
  return (
    <ol className={styles.alpsHeaderMenus}>
      {data
        ?.filter((item) => item.active)
        .map(({ id, href, label }) => {
          return (
            <li key={id}>
              <a href={href} title={label}>
                {label}
              </a>
            </li>
          );
        })}
    </ol>
  );
};

export default Header;
